import {createUuid} from '../lib/create-uuid'
import type {QuoteRequirement, QuoteRequirementKind} from './quote-builder-logic'

function createRequirementId(): string {
    return createUuid()
}

export function addRequirement(
    requirements: QuoteRequirement[],
    lineId: string,
    kind: QuoteRequirementKind,
): QuoteRequirement[] {
    const requirement: QuoteRequirement =
        kind === 'worker'
            ? {
                  id: createRequirementId(),
                  lineId,
                  kind: 'worker',
                  workerTypeId: '',
                  qty: 1,
              }
            : {
                  id: createRequirementId(),
                  lineId,
                  kind: 'equipment',
                  rentalAssetTypeId: '',
                  qty: 1,
              }

    return [...requirements, requirement]
}

export function removeRequirement(
    requirements: QuoteRequirement[],
    id: string,
): QuoteRequirement[] {
    return requirements.filter((requirement) => requirement.id !== id)
}

export function setRequirementType(
    requirements: QuoteRequirement[],
    id: string,
    typeId: string,
): QuoteRequirement[] {
    return requirements.map((requirement) => {
        if (requirement.id !== id) {
            return requirement
        }

        if (requirement.kind === 'worker') {
            return {...requirement, workerTypeId: typeId}
        }

        return {...requirement, rentalAssetTypeId: typeId}
    })
}

export function updateRequirementQty(
    requirements: QuoteRequirement[],
    id: string,
    qty: number,
): QuoteRequirement[] {
    const nextQty = Math.max(1, Math.floor(qty))

    return requirements.map((requirement) =>
        requirement.id === id ? {...requirement, qty: nextQty} : requirement,
    )
}

export function requirementsForLine(
    requirements: QuoteRequirement[],
    lineId: string,
): QuoteRequirement[] {
    return requirements.filter((requirement) => requirement.lineId === lineId)
}

export function requirementCount(requirements: QuoteRequirement[], lineId: string): number {
    return requirementsForLine(requirements, lineId).length
}

export function isLineMapped(requirements: QuoteRequirement[], lineId: string): boolean {
    return requirementCount(requirements, lineId) > 0
}

export function removeRequirementsForLine(
    requirements: QuoteRequirement[],
    lineId: string,
): QuoteRequirement[] {
    return requirements.filter((requirement) => requirement.lineId !== lineId)
}

export type AggregatedMappedRequirement = {
    kind: QuoteRequirementKind
    typeId: string
    qty: number
}

export function aggregateMappedRequirements(
    requirements: QuoteRequirement[],
): AggregatedMappedRequirement[] {
    const aggregated = new Map<string, AggregatedMappedRequirement>()

    for (const requirement of requirements) {
        const typeId =
            requirement.kind === 'worker' ? requirement.workerTypeId : requirement.rentalAssetTypeId
        if (!typeId) {
            continue
        }

        const key = `${requirement.kind}:${typeId}`
        const existing = aggregated.get(key)
        if (existing == null) {
            aggregated.set(key, {kind: requirement.kind, typeId, qty: requirement.qty})
            continue
        }

        aggregated.set(key, {...existing, qty: existing.qty + requirement.qty})
    }

    return [...aggregated.values()]
}

function pluralizeTypeLabel(name: string, qty: number): string {
    const label = name.toLowerCase()
    if (qty === 1) {
        return label
    }

    return label.endsWith('s') ? label : `${label}s`
}

export function formatMappedRequirementSummary(
    summary: AggregatedMappedRequirement,
    typeName: string,
): string {
    const label = pluralizeTypeLabel(typeName, summary.qty)
    if (summary.qty === 1) {
        return `${summary.qty} ${label} is required`
    }

    return `${summary.qty} ${label} are required`
}
